import { type FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  playerBonusesCancellation,
  playerFreespinsCancellation,
} from '@/api/common';
import { Button, ButtonClose } from '@/components/buttons';
import { Modal } from '@/components/Modal';
import { showTooltip } from '@/helpers/tooltip';
import { useGetCommonStore } from '@/store';
import { TooltipTypes } from '@/store/tooltipStore';

import { ScCancelBonusModal, ScCancelBonusModalContainer } from './styled';

export interface CancelBonusModalProps {
  isBonusType: boolean;
  id: number;
}

export const CancelBonusModal: FC<CancelBonusModalProps> = ({
  isBonusType,
  id,
}) => {
  const useCommonStore = useGetCommonStore();

  const fetchBonuses = useCommonStore.use.fetchBonuses();
  const fetchCompPoints = useCommonStore.use.fetchCompPoints();
  const { t } = useTranslation('bonuses');

  const [pending, setPending] = useState(false);

  const bonusType = isBonusType ? 'bonus' : 'freespin';

  const onClose = useCallback(
    () =>
      useCommonStore.setState({
        currentCancelBonusData: undefined,
      }),
    []
  );

  const handleCancelBonus = useCallback(async () => {
    const error = await (async () => {
      let responseError;

      setPending(true);

      if (isBonusType) {
        const { error } = await playerBonusesCancellation(id);

        if (!error) await fetchBonuses();

        responseError = error;
      } else {
        const { error } = await playerFreespinsCancellation(id);

        if (!error) await fetchCompPoints();

        responseError = error;
      }

      return responseError;
    })();

    if (error) {
      showTooltip({
        type: TooltipTypes.ERROR,
        body: t('sthWentWrong'),
      });
    } else {
      showTooltip({
        type: TooltipTypes.SUCCESS,
        body: t('tooltip.bonusCancelSuccess'),
      });
    }

    setPending(false);

    onClose();
  }, [onClose, isBonusType, id, fetchBonuses, fetchCompPoints, bonusType, t]);

  return (
    <Modal>
      <ScCancelBonusModalContainer>
        <ScCancelBonusModal>
          <ButtonClose dark onClick={onClose} right={8} top={8} />

          <div className="text-content">
            <div className="title">{t('cancelBonusModal.title')}</div>
            <div className="warning">{t('cancelBonusModal.descr')}</div>
          </div>

          <div className="btn-container">
            <Button
              blackTransparent
              disabled={pending}
              noMinWidth
              onClick={handleCancelBonus}
              wide
            >
              {t('cancelBonusModal.yes')}
            </Button>
            <Button
              disabled={pending}
              noMinWidth
              onClick={onClose}
              primary
              wide
            >
              {t('cancelBonusModal.no')}
            </Button>
          </div>
        </ScCancelBonusModal>
      </ScCancelBonusModalContainer>
    </Modal>
  );
};
